import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Container,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { login, clearError } from '../store/slices/authSlice';
import { LANG_STORAGE_KEY } from '../i18n';

interface LoginForm {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading, error, token, user } = useAppSelector(state => state.auth);
  const [showRegister, setShowRegister] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem(LANG_STORAGE_KEY) || 'en');
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from || '/dashboard';
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (token && user) {
      navigate(from, { replace: true });
    }
  }, [token, user, from, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>({ key: lang } as any);

  const onSubmit = (data: LoginForm) => {
    dispatch(clearError());
    dispatch(login(data));
  };

  const handleLangChange = (newLang: string) => {
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                value={lang}
                exclusive
                onChange={(_e, newLang) => newLang && handleLangChange(newLang)}
                aria-label={t('lang.switchLanguage')}
              >
                <ToggleButton value="en" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                  {t('lang.en')}
                </ToggleButton>
                <ToggleButton value="fr" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                  {t('lang.fr')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src="/logo/ebola_logo.png"
                  alt="Ebola"
                  style={{ height: '90px', width: '260px', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                {t('auth.signInTitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                fullWidth
                label={t('auth.email')}
                type="email"
                margin="normal"
                {...register('email', {
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: t('auth.emailInvalid'),
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />

              <TextField
                fullWidth
                label={t('auth.password')}
                type="password"
                margin="normal"
                {...register('password', {
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.passwordMinLength'),
                  },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : t('auth.signIn')}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                href="/forgot-password"
                variant="body2"
                sx={{ display: 'block', mb: 1 }}
              >
                {t('auth.forgotPassword')}
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowRegister(!showRegister)}
              >
                {showRegister ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}
              </Link>
            </Box>

            {showRegister && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.registerContact')}
                </Typography>
              </Box>
            )}

          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginPage;
